import { AsyncPipe, NgClass } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    OnDestroy,
    OnInit,
    Type,
    ViewChild,
    inject,
} from '@angular/core';
import { EnvironmentService } from '@core/services/environment.service';
import { Observable, ReplaySubject, Subject, combineLatest } from 'rxjs';
import {
    distinctUntilChanged,
    map,
    shareReplay,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { ModalDirective } from '../../directives/modal.directive';
import { ModalData } from '../../interfaces/modal-data';
import { ModalService } from '../../services/modal.service';

@Component({
    selector: 'app-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.css'],
    imports: [NgClass, ModalDirective, AsyncPipe],
})
export class ModalComponent implements OnInit, OnDestroy, AfterViewInit {
    private modalService = inject(ModalService);
    private environmentService = inject(EnvironmentService);
    private cd = inject(ChangeDetectorRef);

    active: Observable<boolean>;
    destroy = new Subject();
    styles: Observable<string[]>;
    modalContainerstyles: Observable<string[]>;
    @ViewChild(ModalDirective, { static: true }) appModal: ModalDirective;
    showBackButton = new ReplaySubject<boolean>(1);
    canHide = new ReplaySubject<boolean>(1);
    instance: Observable<any[]>;
    instanceStyles: Observable<string[]>;
    navigationStyles: Observable<string[]>;
    animate = false;

    modalContainerCombinedClass: Observable<string>;
    modalRootClass: Observable<string>;
    isModalFullscreen: Observable<boolean>;

    ngOnInit(): void {
        this.active = this.modalService.active.pipe(
            tap(isActive =>
                isActive && this.environmentService.isBrowser()
                    ? (this.animate = true)
                    : ''
            )
        );
    }

    ngOnDestroy(): void {
        this.modalService.hide();
        this.destroy.next(null);
        this.destroy.complete();
    }

    ngAfterViewInit(): void {
        this.instance = this.modalService.component.pipe(
            distinctUntilChanged(
                (previous, current) =>
                    previous[0] === current[0] && previous[1] === current[1]
            ),
            map(([component, data, back, canHide]) => [
                this.buildComponent(component),
                data,
                back,
                canHide,
            ]),
            tap(([componentRef, data, back, canHide]) =>
                this.showBackButton.next(back)
            ),
            tap(([componentRef, data, back, canHide]) =>
                this.canHide.next(canHide)
            ),
            tap(
                ([component, data, back, canHide]) =>
                    (component.instance.modalData = data)
            ),
            shareReplay(1),
            takeUntil(this.destroy)
        );
        this.instance.subscribe(([component, data, back]) => {
            this.cd.detectChanges();
        });
        this.instanceStyles = this.instance.pipe(
            map(
                ([component, data, back]) =>
                    (component?.instance?.modalStyle
                        ? component.instance.modalStyle
                        : []) as string[]
            ),
            shareReplay(1)
        );
        this.navigationStyles = this.instance.pipe(
            map(
                ([component, data, back]) =>
                    (component?.instance?.navigationStyle
                        ? component.instance.navigationStyle
                        : []) as string[]
            ),
            shareReplay(1)
        );
        this.modalContainerstyles = this.instance.pipe(
            map(
                ([component, data, back]) =>
                    (component?.instance?.modalContainerstyles
                        ? component.instance.modalContainerstyles
                        : []) as string[]
            ),
            shareReplay(1)
        );
        this.modalRootClass = this.instance.pipe(
            map(([component, data, back]) => {
                console.log(component.instance);
                return (
                    component?.instance?.modalRootStyles
                        ? component.instance.modalRootStyles
                        : []
                ).join(' ') as string;
            }),
            shareReplay(1)
        );
        this.isModalFullscreen = this.instance.pipe(
            map(([component]) => {
                return component?.instance?.isModalFullscreen;
            }),
            shareReplay(1)
        );
        this.styles = this.showBackButton.pipe(
            map(showBackButton =>
                showBackButton ? ['rounded-fa'] : ['rounded-fa rounded-tl-faxl']
            )
        );
        this.modalContainerCombinedClass = combineLatest([
            this.styles,
            this.modalContainerstyles,
        ]).pipe(
            map(([styles, modalContainerstyles]) => {
                return [...styles, ...modalContainerstyles].join(' ');
            })
        );
    }
    hide() {
        this.canHide.pipe(take(1)).subscribe(canHide => {
            if (canHide) {
                this.modalService.hide();
                setTimeout(() => {
                    this.animate = false;
                    this.appModal.viewContainerRef.clear();
                }, 200);
            }
        });
    }

    back() {
        this.modalService.back();
    }

    buildComponent<T>(
        componentType: Type<ModalData<T>>
    ): ComponentRef<ModalData<T>> {
        const viewContainerRef = this.appModal.viewContainerRef;
        viewContainerRef.clear();
        return viewContainerRef.createComponent<ModalData<T>>(componentType);
    }
}
